import {FloorAdCodes, SESSION_STORAGE_KEY} from '@shared/Ads/Yandex/consts';
import {safeSessionStorage} from '@utils/safeStorages';
import {useEffect, useState} from 'react';

import {
  FloorAdProps,
  TEmitYmEvent,
  TLogTmrEvent,
} from '@shared/Ads/Yandex/types';

import {useAnalytics} from '@shared/Ads/Yandex/hooks/AdFox/useAnalytics';
import {useAutorefresh} from '@shared/Ads/Yandex/hooks/AdFox/useAutorefresh';
import {useAwakenController} from '@shared/Ads/Yandex/hooks/AdFox/useAwakenController';
import {useStats} from '@shared/Ads/Yandex/hooks/AdFox/useStats';

import {useAdfoxStore} from '@shared/Ads/Yandex/store';

import {footerPaddings} from '@shared/Ads/Yandex/utils';
import {combineAndExtendCallbacks} from '@shared/Ads/Yandex/utils/combineAndExtendCallbacks';

type Props = {
  config?: {
    intervalMs: number;
    triggers: ('timer' | 'window_focus' | 'navigation')[];
    logging?: boolean | undefined;
  };
  vkId?: number;
  packageName?: string | null;
  logTmrEvent: TLogTmrEvent;
  emitYmEvent: TEmitYmEvent;
} & FloorAdProps;
const containerId = 'floor_ad';

export const AdFoxFloor = ({mode, config, ...analytics}: Props) => {
  const [adWasAlreadyClosedInSession] = useState(() =>
    safeSessionStorage.getItem(SESSION_STORAGE_KEY),
  );
  const canShowAd = mode === 'floor_ad' || !adWasAlreadyClosedInSession;

  useAwakenController(canShowAd);

  const isScriptLoaded = useAdfoxStore().adFoxScriptState.state === 'loaded';

  const {callbacksFactory: createAutorefreshCallbacks, getRefreshNumber} =
    useAutorefresh({
      containerId,
      isFloorAd: true,
      enabled: canShowAd,
      code: FloorAdCodes,
      config,
    });

  const createAnalyticsCallbacks = useAnalytics({
    adType: mode,
    position: undefined,
    ...analytics,
  });

  const createStatsCallbacks = useStats({getRefreshNumber});

  useEffect(() => {
    if (!canShowAd || !isScriptLoaded) {
      return;
    }

    const adfox = window.Ya?.adfoxCode;
    if (!adfox) {
      return;
    }

    const platform = adfox.getPlatform();
    const code = FloorAdCodes[platform];
    const {callbacks, cleanup} = combineAndExtendCallbacks({
      containerId,
      code,
      factories: [
        createAnalyticsCallbacks,
        createStatsCallbacks,
        createAutorefreshCallbacks,
      ],
      callback: {
        onRender: (data) => {
          footerPaddings.add(data.floorAdSize?.height);
        },
        onClose: () => {
          safeSessionStorage.setItem(SESSION_STORAGE_KEY, '1');
          footerPaddings.remove();
          window.Ya?.adfoxCode?.destroy(containerId);
        },
      },
    });

    window.yaContextCb?.push(() => {
      adfox.create({...code, containerId, ...callbacks});
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded]);

  if (!canShowAd) {
    return null;
  }
  return <div id={containerId} />;
};
